import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { gamificationStore, type GameId } from '../stores/gamificationStore'
import { Button } from '../components/ui/Button'
import { cn } from '../lib/cn'

const SKILL_TAGS = [
  { key: 'home.skillMemory', emoji: '🧠', tint: 'bg-violet-50 text-violet-700 border-violet-100' },
  { key: 'home.skillSpeed', emoji: '⚡', tint: 'bg-amber-50 text-amber-700 border-amber-100' },
  { key: 'home.skillLogic', emoji: '♟️', tint: 'bg-sky-50 text-sky-700 border-sky-100' },
  { key: 'home.skillQuiz', emoji: '📚', tint: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
] as const

type GameCardStyle = {
  bg: string
  border: string
  skillTint: string
  arrowBg: string
}

const GAME_STYLES: Record<string, GameCardStyle> = {
  memoji: { bg: '#faf8ff', border: '#e8e4f2', skillTint: 'bg-violet-50/90 text-violet-600 border-violet-100', arrowBg: 'linear-gradient(135deg, #b8a9d4 0%, #a89bc8 100%)' },
  findmoji: { bg: '#f4fbf7', border: '#d8ebe3', skillTint: 'bg-emerald-50/90 text-emerald-600 border-emerald-100', arrowBg: 'linear-gradient(135deg, #9dc4b0 0%, #8ab5a0 100%)' },
  hitmoji: { bg: '#fff8f4', border: '#f0e4da', skillTint: 'bg-orange-50/90 text-orange-600 border-orange-100', arrowBg: 'linear-gradient(135deg, #d4b8a8 0%, #c9a896 100%)' },
  chessmoji: { bg: '#fffbf3', border: '#efe8d6', skillTint: 'bg-amber-50/90 text-amber-700 border-amber-100', arrowBg: 'linear-gradient(135deg, #d4c4a0 0%, #c9b890 100%)' },
  evoji: { bg: '#fff5f8', border: '#f0dde5', skillTint: 'bg-rose-50/90 text-rose-600 border-rose-100', arrowBg: 'linear-gradient(135deg, #d4a8b8 0%, #c998a8 100%)' },
  quizmoji: { bg: '#f4f8ff', border: '#dce6f2', skillTint: 'bg-sky-50/90 text-sky-600 border-sky-100', arrowBg: 'linear-gradient(135deg, #a8bdd4 0%, #98aec8 100%)' },
  timeline: { bg: '#fff7f4', border: '#f0e0d8', skillTint: 'bg-orange-50/90 text-orange-600 border-orange-100', arrowBg: 'linear-gradient(135deg, #d4b0a0 0%, #c9a090 100%)' },
  worldmoji: { bg: '#f4f7ff', border: '#dce4f2', skillTint: 'bg-indigo-50/90 text-indigo-600 border-indigo-100', arrowBg: 'linear-gradient(135deg, #a8b4d4 0%, #98a8c8 100%)' },
  mojiland: { bg: '#f2faf9', border: '#d4ebe8', skillTint: 'bg-teal-50/90 text-teal-600 border-teal-100', arrowBg: 'linear-gradient(135deg, #9dc4be 0%, #8ab5ae 100%)' },
}

const getGames = (t: (k: string) => string): Array<{ id: GameId; to: string; emoji: string; title: string; desc: string; skill: string; comingSoon?: boolean }> => [
  { id: 'memoji', to: '/memoji', emoji: '🧠', title: t('games.memoji.title'), desc: t('games.memoji.desc'), skill: t('home.skillMemory') },
  { id: 'findmoji', to: '/findmoji', emoji: '👀', title: t('games.findmoji.title'), desc: t('games.findmoji.desc'), skill: t('home.skillSpeed') },
  { id: 'hitmoji', to: '/hitmoji', emoji: '💥', title: t('games.hitmoji.title'), desc: t('games.hitmoji.desc'), skill: t('home.skillSpeed') },
  { id: 'chessmoji', to: '/chessmoji', emoji: '♟️', title: t('games.chessmoji.title'), desc: t('games.chessmoji.desc'), skill: t('home.skillLogic') },
  { id: 'evoji', to: '/evoji', emoji: '🧬', title: t('games.evoji.title'), desc: t('games.evoji.desc'), skill: t('home.skillLogic') },
  { id: 'quizmoji', to: '/quizmoji', emoji: '📚', title: t('games.quizmoji.title'), desc: t('games.quizmoji.desc'), skill: t('home.skillQuiz') },
  { id: 'timeline', to: '/timeline', emoji: '🦁', title: t('games.timeline.title'), desc: t('games.timeline.desc'), skill: t('home.skillQuiz') },
]

const GameCard = observer(({
  gameId,
  cardStyleKey,
  to,
  emoji,
  title,
  desc,
  skill,
  comingSoon = false,
}: {
  gameId?: GameId
  cardStyleKey?: string
  to: string
  emoji: string
  title: string
  desc: string
  skill: string
  comingSoon?: boolean
}) => {
  const { t } = useTranslation()
  const stats = gameId ? gamificationStore.getStatsForGame(gameId) : { bestScore: 0, maxLevel: 0, gamesPlayed: 0 }
  const hasPlayed = !comingSoon && gameId && stats.gamesPlayed > 0
  const style = GAME_STYLES[cardStyleKey ?? gameId ?? 'worldmoji'] ?? GAME_STYLES.worldmoji

  return (
    <Link
      to={comingSoon ? '#' : to}
      className={cn(
        'group block w-full relative overflow-hidden rounded-2xl',
        'transition-all duration-300 ease-out border shadow-sm',
        comingSoon
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm'
      )}
      style={{
        backgroundColor: style.bg,
        borderColor: style.border,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
      }}
    >
      {/* Decorative emoji — centered in content area, clear of arrow column */}
      <div className="absolute inset-y-0 left-0 right-14 flex items-center justify-center pointer-events-none">
        <span
          className="text-7xl sm:text-8xl select-none transition-transform duration-300 group-hover:scale-105"
          style={{ opacity: 0.2 }}
          aria-hidden
        >
          {emoji}
        </span>
      </div>

      <div className="relative z-10 flex items-center gap-3 p-4 sm:p-5">
        <div
          className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl bg-white border shadow-sm"
          style={{ borderColor: style.border }}
        >
          {emoji}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <h3 className="font-title text-lg sm:text-xl font-extrabold text-gray-800 truncate">{title}</h3>
            <span className={cn('shrink-0 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border', style.skillTint)}>
              {skill}
            </span>
          </div>
          <p className="text-gray-600 text-sm line-clamp-2 font-medium">{desc}</p>
          {hasPlayed && !comingSoon && (
            <div className="flex flex-wrap gap-2 mt-2">
              {stats.bestScore > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/90 px-2 py-0.5 rounded-full text-amber-700 border border-amber-100">
                  🏆 {stats.bestScore}
                </span>
              )}
              {stats.maxLevel > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/90 px-2 py-0.5 rounded-full text-sky-700 border border-sky-100">
                  ⬆️ {t('common.levelN', { n: stats.maxLevel + 1 })}
                </span>
              )}
              <span className="inline-flex items-center text-xs font-medium text-gray-500">
                {t('home.gamesPlayed', { count: stats.gamesPlayed })}
              </span>
            </div>
          )}
        </div>

        {comingSoon ? (
          <span className="shrink-0 text-xs font-semibold bg-white/90 px-3 py-1.5 rounded-full text-gray-500 border border-gray-200">
            {t('common.pronto')}
          </span>
        ) : (
          <span
            className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-white text-lg font-bold shadow-sm group-hover:scale-105 transition-transform"
            style={{ background: style.arrowBg }}
          >
            →
          </span>
        )}
      </div>
    </Link>
  )
})

const StatBadge = ({ emoji, value, label }: { emoji: string; value: number | string; label: string }) => (
  <div className="flex flex-col items-center">
    <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center text-xl border border-gray-100 shadow-sm">
      {emoji}
    </div>
    <p className="font-extrabold text-gray-800 text-sm mt-1">{value}</p>
    <p className="text-xs text-gray-500 font-medium">{label}</p>
  </div>
)

const Home = observer(() => {
  const { t } = useTranslation()
  const { level, xpProgress, xpInCurrentLevel, xpToNextLevel } = gamificationStore
  const xpRemaining = Math.max(0, xpToNextLevel - xpInCurrentLevel.current)

  return (
    <div className="min-h-[calc(100vh-64px)] w-full relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(160deg,#fafbff_0%,#fefaf6_45%,#f6faf8_100%)]" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-[radial-gradient(circle,rgba(180,170,210,0.12)_0%,transparent_70%)] blur-2xl" />
        <div className="absolute top-1/3 -right-20 w-72 h-72 rounded-full bg-[radial-gradient(circle,rgba(210,180,190,0.1)_0%,transparent_70%)] blur-2xl" />
        <div className="absolute bottom-0 left-1/4 w-80 h-40 rounded-full bg-[radial-gradient(circle,rgba(170,200,210,0.08)_0%,transparent_70%)] blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 py-6 pb-24">
        {/* Hero */}
        <div className="text-center mb-6 pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-white/90 border border-slate-200/80 text-slate-600 mb-3">
            ✨ {t('home.heroBadge')}
          </span>
          <h1 className="font-title text-4xl sm:text-5xl font-extrabold tracking-tight mb-2">
            <span className="text-transparent bg-clip-text bg-[linear-gradient(135deg,#6b7cb8_0%,#9b8ec4_50%,#c4a8b0_100%)]">
              {t('home.title')}
            </span>
          </h1>
          <p className="text-gray-600 font-medium text-base sm:text-lg max-w-md mx-auto">{t('home.subtitle')}</p>

          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {SKILL_TAGS.map(({ key, emoji, tint }) => (
              <span
                key={key}
                className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border',
                  tint
                )}
              >
                {emoji} {t(key)}
              </span>
            ))}
          </div>
        </div>

        {/* XP bar */}
        <div
          className="mb-5 rounded-2xl p-4 relative overflow-hidden border border-amber-100/80 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, #fffef8 0%, #fff9f0 50%, #ffffff 100%)',
          }}
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-800 font-extrabold flex items-center gap-2 font-title text-lg">
                <span
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-base shadow-sm border border-amber-100"
                  style={{ background: 'linear-gradient(135deg, #f5edd8 0%, #ede4c8 100%)' }}
                >
                  ⭐
                </span>
                {t('common.level')} {level}
              </span>
              <span className="text-amber-800/80 text-xs sm:text-sm font-semibold bg-white/80 px-2 py-1 rounded-full border border-amber-100">
                {t('home.xpToNext', { xp: xpRemaining })}
              </span>
            </div>
            <div className="h-3 rounded-full bg-amber-50/80 overflow-hidden border border-amber-100/70">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${Math.round(xpProgress * 100)}%`,
                  background: 'linear-gradient(90deg, #e8d9a8 0%, #dcc99a 100%)',
                }}
              />
            </div>
            <p className="text-right text-xs font-medium text-amber-800/60 mt-1.5">
              {xpInCurrentLevel.current} / {xpToNextLevel} {t('common.xp')}
            </p>
          </div>
        </div>

        {/* Mojiland CTA */}
        <Link
          to="/mojiland"
          className="block mb-6 rounded-2xl p-4 relative overflow-hidden border border-teal-100 bg-[#f2faf9] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all"
        >
          <div className="absolute inset-y-0 left-0 right-20 flex items-center justify-center pointer-events-none">
            <span className="text-7xl opacity-[0.15] select-none" aria-hidden>🏝️</span>
          </div>
          <div className="relative z-10 flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-sm border border-teal-100"
              style={{ background: 'linear-gradient(135deg, #d4ebe8 0%, #c5e0dc 100%)' }}
            >
              🌊
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-title font-extrabold text-gray-800 text-lg">{t('home.academyCta')}</p>
              <p className="text-sm text-gray-600 font-medium">{t('home.academyCtaDesc')}</p>
            </div>
            <span className="hidden sm:inline text-sm font-semibold text-teal-700 bg-white/90 px-3 py-1.5 rounded-xl border border-teal-100">
              {t('home.academyCtaButton')}
            </span>
          </div>
        </Link>

        {/* Games list */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl">🎮</span>
          <h2 className="font-title font-extrabold text-gray-700 text-lg">{t('home.featuredGames')}</h2>
        </div>
        <div className="space-y-3 mb-10">
          {getGames(t).map((game) => (
            <GameCard
              key={game.id}
              gameId={game.id}
              to={game.to}
              emoji={game.emoji}
              title={game.title}
              desc={game.desc}
              skill={game.skill}
              comingSoon={game.comingSoon}
            />
          ))}
        </div>

        {/* Profile card */}
        <div
          className="rounded-2xl p-5 mb-8 relative overflow-hidden border border-slate-200/60 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, #fafbff 0%, #f8f6fc 50%, #faf8f6 100%)',
          }}
        >
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl border-2 border-white shadow-sm"
                  style={{ background: 'linear-gradient(135deg, #c8b8e0 0%, #d4b8c8 100%)' }}
                >
                  {profileStore.emoji}
                </div>
                <span className="absolute -bottom-1 -right-1 text-base">{profileStore.flag}</span>
              </div>
              <div className="flex-1">
                <p className="font-title font-extrabold text-gray-800 text-lg">
                  {profileStore.username || t('common.player')}
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="text-xs px-2 py-0.5 bg-white/90 text-amber-700 rounded-full font-semibold border border-amber-100">
                    {t('common.level')} {level}
                  </span>
                  <span className="text-xs text-gray-600 font-semibold">
                    🔥 {profileStore.scoreForm} {t('common.pts')}
                  </span>
                </div>
              </div>
              <Button as="link" to="/profile" variant="ghost" size="sm" className="!bg-white/90 !border !border-gray-200 !shadow-sm !rounded-xl !p-2 !min-w-0">
                ⚙️
              </Button>
            </div>
            <div className="flex justify-around pt-4 border-t border-gray-100">
              {Object.entries(profileStore.resources).slice(0, 4).map(([icon, amount]) => (
                <StatBadge key={icon} emoji={icon} value={amount} label={t('common.resources')} />
              ))}
            </div>
          </div>
        </div>

        {/* Coming soon */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🚀</span>
            <h2 className="font-title font-extrabold text-gray-500">{t('home.comingSoon')}</h2>
          </div>
          <div className="space-y-3">
            <GameCard
              cardStyleKey="worldmoji"
              to="#"
              emoji="🌍"
              title={t('games.worldmoji.title')}
              desc={t('games.worldmoji.desc')}
              skill={t('home.skillLogic')}
              comingSoon
            />
            <GameCard
              cardStyleKey="mojiland"
              to="/mojiland"
              emoji="🛖"
              title={t('games.mojiland.title')}
              desc={t('games.mojiland.desc')}
              skill={t('home.skillQuiz')}
            />
          </div>
        </div>

        <div className="text-center">
          <p className="text-gray-500 text-sm">{t('home.madeWith')}</p>
          <p className="text-gray-400 text-xs mt-1">{t('home.mobileWeb')}</p>
        </div>
      </div>
    </div>
  )
})

export default Home
